"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DialogHost = exports.askText = exports.askFields = exports.askConfirm = exports.DIALOG_EVENT = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const responsive_1 = require("./responsive");
exports.DIALOG_EVENT = 'mango:dialog';
let nextId = 1;
function send(request) {
    window.dispatchEvent(new CustomEvent(exports.DIALOG_EVENT, { detail: { ...request, id: nextId++ } }));
}
/** "Delete this? It can't be undone." -> title "Delete this?", message "It can't be undone." */
function splitQuestion(text) {
    const cut = text.search(/[?!.](\s|$)/);
    if (cut < 0 || cut >= text.length - 2)
        return { title: text };
    return { title: text.slice(0, cut + 1), message: text.slice(cut + 1).trim() };
}
/**
 * Resolves true when confirmed, false when cancelled. A plain string is split into a short title
 * and the explanation under it; `extra` adds a button label / danger styling.
 */
function askConfirm(options, extra) {
    const opts = { ...(typeof options === 'string' ? splitQuestion(options) : options), ...extra };
    if (typeof window === 'undefined')
        return Promise.resolve(false);
    return new Promise((resolve) => send({ kind: 'confirm', options: opts, resolve }));
}
exports.askConfirm = askConfirm;
/** Several labelled fields in one sheet. Resolves to trimmed values by key, or null if cancelled. */
function askFields(options) {
    if (typeof window === 'undefined')
        return Promise.resolve(null);
    return new Promise((resolve) => send({ kind: 'fields', options, resolve }));
}
exports.askFields = askFields;
/** One text box. Resolves to the trimmed text (may be ''), or null if cancelled. */
async function askText(options) {
    const { title, message, label, confirmLabel, cancelLabel, ...field } = options;
    const values = await askFields({
        title,
        message,
        confirmLabel,
        cancelLabel,
        fields: [{ key: 'value', label: label ?? '', ...field }],
    });
    return values ? values.value : null;
}
exports.askText = askText;
const PRIMARY = '#667eea';
const DANGER = '#dc2626';
function DialogHost() {
    const [queue, setQueue] = (0, react_1.useState)([]);
    const current = queue[0];
    (0, react_1.useEffect)(() => {
        const onRequest = (e) => setQueue((prev) => [...prev, e.detail]);
        window.addEventListener(exports.DIALOG_EVENT, onRequest);
        return () => window.removeEventListener(exports.DIALOG_EVENT, onRequest);
    }, []);
    const close = () => setQueue((prev) => prev.slice(1));
    if (!current)
        return null;
    if (current.kind === 'confirm') {
        return ((0, jsx_runtime_1.jsx)(Sheet, { title: current.options.title, message: current.options.message, confirmLabel: current.options.confirmLabel ?? (current.options.danger ? 'Delete' : 'OK'), cancelLabel: current.options.cancelLabel ?? 'Cancel', danger: current.options.danger, hideCancel: current.options.infoOnly, onDone: (ok) => {
                current.resolve(ok);
                close();
            } }, current.id));
    }
    return ((0, jsx_runtime_1.jsx)(Sheet, { title: current.options.title, message: current.options.message, fields: current.options.fields, confirmLabel: current.options.confirmLabel ?? 'Save', cancelLabel: current.options.cancelLabel ?? 'Cancel', onDone: (ok, values) => {
            current.resolve(ok ? values ?? {} : null);
            close();
        } }, current.id));
}
exports.DialogHost = DialogHost;
function Sheet({ title, message, fields, confirmLabel, cancelLabel, danger, hideCancel, onDone }) {
    const isPhone = (0, responsive_1.useIsMobile)();
    const [values, setValues] = (0, react_1.useState)(() => Object.fromEntries((fields ?? []).map((f) => [f.key, f.defaultValue ?? ''])));
    const [shown, setShown] = (0, react_1.useState)(false);
    const firstInput = (0, react_1.useRef)(null);
    const confirmButton = (0, react_1.useRef)(null);
    const missing = (fields ?? []).some((f) => f.required && !values[f.key]?.trim());
    const finish = (ok) => {
        if (ok && missing)
            return;
        const trimmed = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]));
        onDone(ok, trimmed);
    };
    (0, react_1.useEffect)(() => {
        const raf = requestAnimationFrame(() => setShown(true));
        (firstInput.current ?? confirmButton.current)?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape')
                onDone(false);
        };
        window.addEventListener('keydown', onKey);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const inputStyle = {
        width: '100%',
        padding: '12px 14px',
        border: '1px solid #d1d5db',
        borderRadius: 10,
        fontSize: 16,
        boxSizing: 'border-box',
        fontFamily: 'inherit',
    };
    return ((0, jsx_runtime_1.jsx)("div", { "data-dialog-host": true, onClick: () => onDone(false), style: {
            position: 'fixed',
            inset: 0,
            zIndex: 11000,
            background: shown ? 'rgba(15,23,42,0.45)' : 'rgba(15,23,42,0)',
            transition: 'background 160ms ease',
            display: 'flex',
            alignItems: isPhone ? 'flex-end' : 'center',
            justifyContent: 'center',
        }, children: (0, jsx_runtime_1.jsxs)("form", { role: "dialog", "aria-modal": "true", "aria-label": title, onClick: (e) => e.stopPropagation(), onSubmit: (e) => {
                e.preventDefault();
                finish(true);
            }, style: {
                background: 'white',
                width: isPhone ? '100%' : 'min(440px, calc(100% - 32px))',
                maxHeight: isPhone ? '90vh' : '85vh',
                overflowY: 'auto',
                borderRadius: isPhone ? '18px 18px 0 0' : 14,
                padding: isPhone ? '10px 20px calc(20px + env(safe-area-inset-bottom))' : 24,
                boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
                transform: shown ? 'none' : isPhone ? 'translateY(40px)' : 'scale(0.97)',
                opacity: shown ? 1 : 0,
                transition: 'transform 180ms ease, opacity 180ms ease',
            }, children: [isPhone && (0, jsx_runtime_1.jsx)("div", { "aria-hidden": true, style: { width: 40, height: 4, borderRadius: 2, background: '#d1d5db', margin: '0 auto 14px' } }), (0, jsx_runtime_1.jsx)("h2", { style: { margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#111827' }, children: title }), message && (0, jsx_runtime_1.jsx)("div", { style: { margin: '0 0 12px', fontSize: 15, color: '#4b5563', lineHeight: 1.5, whiteSpace: 'pre-line' }, children: message }), (fields ?? []).map((f, i) => ((0, jsx_runtime_1.jsxs)("label", { style: { display: 'block', marginTop: 12 }, children: [f.label && ((0, jsx_runtime_1.jsxs)("span", { style: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }, children: [f.label, !f.required && !f.defaultValue && (0, jsx_runtime_1.jsx)("span", { style: { fontWeight: 400, color: '#9ca3af' }, children: " (optional)" })] })), f.type === 'textarea' ? ((0, jsx_runtime_1.jsx)("textarea", { ref: i === 0 ? (el) => { firstInput.current = el; } : undefined, value: values[f.key], placeholder: f.placeholder, rows: 4, onChange: (e) => setValues((v) => ({ ...v, [f.key]: e.target.value })), style: { ...inputStyle, resize: 'vertical' } })) : ((0, jsx_runtime_1.jsx)("input", { ref: i === 0 ? (el) => { firstInput.current = el; } : undefined, type: f.type ?? 'text', inputMode: f.type === 'number' ? 'decimal' : undefined, value: values[f.key], placeholder: f.placeholder, onChange: (e) => setValues((v) => ({ ...v, [f.key]: e.target.value })), style: inputStyle })), f.hint && (0, jsx_runtime_1.jsx)("span", { style: { display: 'block', fontSize: 12, color: '#6b7280', marginTop: 4 }, children: f.hint })] }, f.key))), (0, jsx_runtime_1.jsxs)("div", { style: {
                        display: 'flex',
                        flexDirection: isPhone ? 'column-reverse' : 'row',
                        justifyContent: 'flex-end',
                        gap: 10,
                        marginTop: 20,
                    }, children: [!hideCancel && ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => onDone(false), style: {
                                padding: '12px 18px',
                                minHeight: 44,
                                borderRadius: 10,
                                border: '1px solid #d1d5db',
                                background: 'white',
                                color: '#374151',
                                fontSize: 15,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }, children: cancelLabel })), (0, jsx_runtime_1.jsx)("button", { ref: confirmButton, type: "submit", disabled: missing, style: {
                                padding: '12px 18px',
                                minHeight: 44,
                                borderRadius: 10,
                                border: 'none',
                                background: missing ? '#c7d2fe' : danger ? DANGER : PRIMARY,
                                color: 'white',
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: missing ? 'not-allowed' : 'pointer',
                            }, children: confirmLabel })] })] }) }));
}
